import { useMemo, useState } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
const MONTHS = Array.from({ length: 12 }, (_, month) => ({
  value: String(month),
  label: new Intl.DateTimeFormat('en-US', { month: 'short' }).format(
    new Date(2026, month, 1)
  ),
}))

interface ScorecardDatePickerProps {
  id?: string
  value?: string
  onChange: (value: string) => void
  onBlur?: () => void
  disabled?: boolean
  className?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}

function parseIsoDate(value?: string | null): Date | null {
  if (!value) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2]) - 1
  const day = Number(match[3])
  const date = new Date(year, month, day)

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null
  }

  return date
}

function toIsoDate(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatDisplayDate(value?: string) {
  const date = parseIsoDate(value)
  if (!date) return 'Select date'

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

function isSameDay(a: Date | null, b: Date) {
  return (
    a?.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function buildCalendarDays(viewMonth: Date) {
  const firstOfMonth = new Date(
    viewMonth.getFullYear(),
    viewMonth.getMonth(),
    1
  )
  const mondayOffset = (firstOfMonth.getDay() + 6) % 7
  const start = new Date(firstOfMonth)
  start.setDate(firstOfMonth.getDate() - mondayOffset)

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start)
    date.setDate(start.getDate() + index)
    return date
  })
}

function buildYearOptions(selectedDate: Date | null, today: Date) {
  const currentYear = today.getFullYear()
  const selectedYear = selectedDate?.getFullYear() ?? currentYear
  const start = Math.min(currentYear - 30, selectedYear - 5)
  const end = Math.max(currentYear + 30, selectedYear + 5)

  return Array.from({ length: end - start + 1 }, (_, index) => start + index)
}

export function ScorecardDatePicker({
  id,
  value,
  onChange,
  onBlur,
  disabled = false,
  className,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: ScorecardDatePickerProps) {
  const [open, setOpen] = useState(false)
  const selectedDate = useMemo(() => parseIsoDate(value), [value])
  const [viewMonth, setViewMonth] = useState(() =>
    selectedDate
      ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
      : new Date(new Date().getFullYear(), 11, 1)
  )
  const today = useMemo(() => new Date(), [])
  const days = useMemo(() => buildCalendarDays(viewMonth), [viewMonth])
  const yearOptions = useMemo(
    () => buildYearOptions(selectedDate, today),
    [selectedDate, today]
  )

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && selectedDate) {
      setViewMonth(
        new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
      )
    }
    setOpen(nextOpen)
  }

  const moveMonth = (delta: number) => {
    setViewMonth((current) => {
      return new Date(current.getFullYear(), current.getMonth() + delta, 1)
    })
  }

  const selectMonth = (month: string) => {
    setViewMonth((current) => {
      return new Date(current.getFullYear(), Number(month), 1)
    })
  }

  const selectYear = (year: string) => {
    setViewMonth((current) => new Date(Number(year), current.getMonth(), 1))
  }

  const selectDate = (date: Date) => {
    onChange(toIsoDate(date))
    onBlur?.()
    setOpen(false)
  }

  return (
    // Modal so the capped calendar scrolls: dialog scroll locks swallow wheel events on portals.
    <Popover modal open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-9 w-full justify-between border-primary/20 bg-transparent px-3 text-left text-xs font-normal text-primary hover:bg-primary/5 focus-visible:ring-1 focus-visible:ring-ring',
            !selectedDate && 'text-primary/70',
            className
          )}
          onBlur={onBlur}
        >
          <span className="truncate">{formatDisplayDate(value)}</span>
          <CalendarDays className="size-4 text-primary/70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={16}
        className="w-80 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-0 scroll-pt-14"
      >
        <div className="sticky top-0 z-10 grid grid-cols-[2rem_1fr_5.25rem_2rem] items-center gap-2 bg-popover p-3">
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="h-8 w-8 rounded-full"
            onClick={() => moveMonth(-1)}
            aria-label="Previous month"
          >
            <ChevronLeft className="size-4" />
          </Button>

          <Select
            value={String(viewMonth.getMonth())}
            onValueChange={selectMonth}
          >
            <SelectTrigger
              variant="ghost"
              className="h-8 border-primary/10 bg-white px-2 text-xs font-medium text-primary"
              aria-label="Select month"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="item-aligned" className="max-h-72">
              {MONTHS.map((month) => (
                <SelectItem key={month.value} value={month.value}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={String(viewMonth.getFullYear())}
            onValueChange={selectYear}
          >
            <SelectTrigger
              variant="ghost"
              className="h-8 border-primary/10 bg-white px-2 text-xs font-medium text-primary"
              aria-label="Select year"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="item-aligned" className="max-h-72">
              {yearOptions.map((year) => (
                <SelectItem key={year} value={String(year)}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="h-8 w-8 rounded-full"
            onClick={() => moveMonth(1)}
            aria-label="Next month"
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>

        <div className="px-3 pb-3">
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-primary/70">
            {WEEKDAYS.map((weekday, index) => (
              <div key={`${weekday}-${index}`} className="py-1">
                {weekday}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((date) => {
              const inMonth = date.getMonth() === viewMonth.getMonth()
              const selected = isSameDay(selectedDate, date)
              const isToday = isSameDay(today, date)

              return (
                <button
                  key={date.toISOString()}
                  type="button"
                  className={cn(
                    'h-8 cursor-pointer rounded-md text-xs tabular-nums transition-colors hover:bg-primary/8 focus:outline-none focus:ring-1 focus:ring-ring',
                    inMonth ? 'text-primary' : 'text-primary/70',
                    isToday && !selected && 'bg-primary/5 text-primary',
                    selected &&
                      'bg-primary text-primary-foreground hover:bg-primary'
                  )}
                  onClick={() => selectDate(date)}
                >
                  {date.getDate()}
                </button>
              )
            })}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-primary/10 pt-3">
            <span className="text-[11px] text-primary/70">
              Pick a day to apply
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-primary/70 hover:text-primary"
              onClick={() => {
                const next = new Date(today)
                setViewMonth(new Date(next.getFullYear(), next.getMonth(), 1))
              }}
            >
              Jump to today
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
